/**
 * Conversation list actions: start a new chat, switch to a session or a
 * recent document, rename and delete sessions (with the rename dialog
 * state).
 */
import { useCallback, useState } from 'react';

import { confirmDialog, showDialog } from '@/utils/dialogs';
import type { ToastType } from '@/components/ui/Toast';
import { ROUTES } from '../../navigation/routes';
import { trackAuthEvent } from '../../services/analytics';
import { chatAPI, transcriptionAPI, type UserDocumentItem } from '../../services/api';
import type { ChatSession } from '../../types';
import { actionErrorMessage, errorStatusOf } from './chatErrors';
import type { ChatRouter, SetMessages, SetState, TFn } from './chatFlowTypes';
import type { ConversationGuard } from './conversationGuard';
import type { ChatOperationRefs } from './useChatOperationRefs';

export function useSessionActions({
  ops,
  guard,
  t,
  router,
  showToast,
  currentSessionId,
  setCurrentSessionId,
  setSessions,
  setMessages,
  setInputText,
  setShowSessionsList,
  showSession,
  clearSession,
  setViewingTranscript,
  setShowTranscriptViewer,
}: {
  ops: ChatOperationRefs;
  guard: ConversationGuard;
  t: TFn;
  router: ChatRouter;
  showToast: (message: string, type?: ToastType) => void;
  currentSessionId: number | null;
  setCurrentSessionId: SetState<number | null>;
  setSessions: SetState<ChatSession[]>;
  setMessages: SetMessages;
  setInputText: SetState<string>;
  setShowSessionsList: SetState<boolean>;
  showSession: (sessionId: number) => void;
  clearSession: () => void;
  setViewingTranscript: SetState<any>;
  setShowTranscriptViewer: SetState<boolean>;
}) {
  const { messageCountRef, pendingScrollToBottomRef, shouldAutoScrollRef } = ops;

  const [showRenameModal, setShowRenameModal] = useState(false);
  // Rename states
  const [sessionToRename, setSessionToRename] = useState<number | null>(null);
  const [newSessionTitle, setNewSessionTitle] = useState('');

  const startNewSession = (source: string = 'unknown') => {
    trackAuthEvent('chat_new_session_started', { source });
    guard.startNew();
    setCurrentSessionId(null);
    setMessages([]);
    setShowSessionsList(false);
    // Clear input if there's any text
    setInputText('');
    shouldAutoScrollRef.current = true;
    pendingScrollToBottomRef.current = true;
    // Reset tip tracking for new session
    messageCountRef.current = 0;
  };

  const switchToSession = (sessionId: number) => {
    trackAuthEvent('chat_session_switched', {
      session_id: sessionId,
      from_session_id: currentSessionId ?? null,
    });
    showSession(sessionId);
    setShowSessionsList(false);
    shouldAutoScrollRef.current = true;
    pendingScrollToBottomRef.current = true;
    // Reset tip tracking for switched session
    messageCountRef.current = 0;
  };

  const openRecentDocument = useCallback(async (item: UserDocumentItem) => {
    trackAuthEvent('chat_recent_document_opened', {
      document_id: item.id,
      document_type: item.document_type,
      session_id: item.session_id ?? null,
    });

    if (item.session_id) {
      showSession(item.session_id);
      setShowSessionsList(false);
      shouldAutoScrollRef.current = true;
      pendingScrollToBottomRef.current = true;
      return;
    }

    if (item.document_type === 'user_transcript') {
      try {
        const transcript = await transcriptionAPI.getContent(item.id);
        setViewingTranscript(transcript);
        setShowTranscriptViewer(true);
        return;
      } catch (error) {
        console.warn('Failed to open recent transcript:', error);
      }
    }

    router.push(ROUTES.TABS_LIBRARY as any);
  }, [
    router, showSession,
    pendingScrollToBottomRef, shouldAutoScrollRef, setShowSessionsList, setShowTranscriptViewer, setViewingTranscript,
  ]);

  const openRenameModal = (sessionId: number, currentTitle: string) => {
    trackAuthEvent('chat_session_rename_started', { session_id: sessionId });
    // Prevent nested modals causing UI freeze by closing sessions list first
    setShowSessionsList(false);
    setSessionToRename(sessionId);
    setNewSessionTitle(currentTitle);
    setShowRenameModal(true);
  };

  const confirmDeleteSession = async (sessionId: number) => {
    const confirmed = await confirmDialog(
      t('chat.deleteConfirmTitle'),
      t('chat.deleteConfirmMessage'),
      t('chat.delete'),
      t('common.cancel'),
    );

    if (confirmed) {
      try {
        trackAuthEvent('chat_session_delete_confirmed', { session_id: sessionId });
        await chatAPI.deleteSession(sessionId);
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        if (guard.viewSessionId === sessionId) {
          // If we deleted the current session, clear selection and messages
          clearSession();
        }
        trackAuthEvent('chat_session_deleted', {
          session_id: sessionId,
          was_current: currentSessionId === sessionId,
        });
        showToast(t('chat.deleteSuccess'), 'success');
      } catch (e: any) {
        const errorStatus = errorStatusOf(e);
        const errorType = errorStatus ? `http_${errorStatus}` : (e?.message ? 'exception' : 'unknown');
        trackAuthEvent('chat_session_delete_failed', {
          session_id: sessionId,
          error_type: errorType,
        });
        showDialog(actionErrorMessage(t, e, 'chat.deleteFailed'));
      }
    }
  };

  const handleRenameSession = async () => {
    if (!sessionToRename || !newSessionTitle.trim()) {
      trackAuthEvent('chat_session_rename_failed', { error_type: 'validation' });
      showDialog(t('chat.enterNewTitle'));
      return;
    }

    try {
      await chatAPI.renameSession(sessionToRename, newSessionTitle.trim());

      // Update local sessions list
      setSessions(prev => prev.map(s =>
        s.id === sessionToRename
          ? { ...s, title: newSessionTitle.trim() }
          : s
      ));

      setShowRenameModal(false);
      setSessionToRename(null);
      setNewSessionTitle('');

      trackAuthEvent('chat_session_renamed', {
        session_id: sessionToRename,
        title_length: newSessionTitle.trim().length,
      });

      showDialog(t('chat.sessionRenamed'));
    } catch (error: any) {
      const errorStatus = errorStatusOf(error);
      const errorType = errorStatus ? `http_${errorStatus}` : (error?.message ? 'exception' : 'unknown');
      trackAuthEvent('chat_session_rename_failed', {
        session_id: sessionToRename ?? null,
        error_type: errorType,
      });
      showDialog(actionErrorMessage(t, error, 'chat.renameFailed'));
    }
  };

  const cancelRename = () => {
    setShowRenameModal(false);
    setSessionToRename(null);
    setNewSessionTitle('');
  };

  return {
    showRenameModal,
    newSessionTitle,
    setNewSessionTitle,
    startNewSession,
    switchToSession,
    openRecentDocument,
    openRenameModal,
    confirmDeleteSession,
    handleRenameSession,
    cancelRename,
  };
}
